import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentPreview = ({ document, onClose, onAction }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const totalPages = 12; // Mock total pages

  const handleZoom = (direction) => {
    if (direction === 'in' && zoomLevel < 200) {
      setZoomLevel(prev => prev + 25);
    } else if (direction === 'out' && zoomLevel > 50) {
      setZoomLevel(prev => prev - 25);
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col bg-background ${isFullscreen ? '' : 'p-4'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} className="text-primary" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{document.name}</h2>
            <p className="text-sm text-muted-foreground">
              Version {document.version} • {document.size} • {document.modified}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('download', document)}
            iconName="Download"
            iconPosition="left"
          >
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('share', document)}
            iconName="Share2"
            iconPosition="left"
          >
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('print', document)}
            iconName="Printer"
            iconPosition="left"
          >
            Print
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-4">
          {/* Page Navigation */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <span className="text-sm text-foreground px-3">
              {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange('next')}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleZoom('out')}
              disabled={zoomLevel <= 50}
            >
              <Icon name="ZoomOut" size={16} />
            </Button>
            <span className="text-sm text-foreground px-3 min-w-[60px] text-center">
              {zoomLevel}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleZoom('in')}
              disabled={zoomLevel >= 200}
            >
              <Icon name="ZoomIn" size={16} />
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleFullscreen}
            iconName={isFullscreen ? "Minimize2" : "Maximize2"}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction('rotate', document)}
            iconName="RotateCw"
          />
        </div>
      </div>

      {/* Document Viewer */}
      <div className="flex-1 overflow-auto bg-muted/30 p-4">
        <div className="max-w-4xl mx-auto">
          <div 
            className="bg-white shadow-legal-lg rounded-lg overflow-hidden"
            style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }}
          >
            {/* Mock PDF Content */}
            <div className="aspect-[8.5/11] p-8 text-sm leading-relaxed">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">MASTER SERVICE AGREEMENT</h1>
                <p className="text-gray-600">Contract No: CT-2024-001</p>
              </div>
              
              <div className="space-y-6 text-gray-800">
                <section>
                  <h2 className="text-lg font-semibold mb-3">1. PARTIES</h2>
                  <p>
                    This Master Service Agreement ("Agreement") is entered into on January 15, 2024, 
                    between TechCorp Solutions Inc., a Delaware corporation ("Company"), and 
                    Global Services LLC, a New York limited liability company ("Service Provider").
                  </p>
                </section>
                
                <section>
                  <h2 className="text-lg font-semibold mb-3">2. SCOPE OF SERVICES</h2>
                  <p>
                    Service Provider agrees to provide software development and consulting services 
                    as detailed in the attached Statement of Work (Exhibit A). Services shall include 
                    but not be limited to:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Custom software development</li>
                    <li>System integration services</li>
                    <li>Technical consulting and support</li>
                    <li>Project management and delivery</li>
                  </ul>
                </section>
                
                <section>
                  <h2 className="text-lg font-semibold mb-3">3. PAYMENT TERMS</h2>
                  <p>
                    Company shall pay Service Provider according to the fee schedule outlined in 
                    Exhibit B. Payment terms are Net 30 days from invoice date. Late payments 
                    may incur a 1.5% monthly service charge.
                  </p>
                </section>
                
                <div className="mt-12 pt-8 border-t border-gray-300">
                  <p className="text-center text-gray-500 text-xs">
                    Page {currentPage} of {totalPages}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Thumbnails Sidebar */}
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 w-20 bg-card border border-border rounded-lg shadow-legal-lg p-2 space-y-2 max-h-96 overflow-y-auto">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index + 1}
            onClick={() => setCurrentPage(index + 1)}
            className={`w-full aspect-[8.5/11] border-2 rounded transition-legal-fast ${
              currentPage === index + 1
                ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
            }`}
          >
            <div className="w-full h-full bg-white rounded flex items-center justify-center">
              <span className="text-xs text-gray-500">{index + 1}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DocumentPreview;